import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { learningService } from '../services/learningService';
import './MorphologyReferencePage.css';

interface Morpheme {
    id: string;
    morpheme: string;
    type: 'prefix' | 'suffix' | 'root';
    category: string;
    meaning: Record<string, string>;
    examples: string[];
    cefrLevel: string;
    sortOrder: number;
}

const TYPE_ORDER = ['prefix', 'suffix', 'root'] as const;

const MorphologyReferencePage: React.FC = () => {
    const { t, i18n } = useTranslation(['morphRef', 'knowledgeGraph']);
    const isEn = i18n.language === 'en' || i18n.language.startsWith('en-');
    const location = useLocation();
    const [morphemes, setMorphemes] = useState<Morpheme[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        learningService.getAllMorphemes()
            .then(setMorphemes)
            .catch(() => { /* ignore */ })
            .finally(() => setLoading(false));
    }, []);

    // Scroll to anchor on load or when data arrives
    useEffect(() => {
        if (location.hash && morphemes.length > 0) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }
        }
    }, [location.hash, morphemes.length]);

    const lang = i18n.language;

    // Group by type → category
    const grouped = TYPE_ORDER.map(type => {
        const items = morphemes.filter(m => m.type === type);
        const categories = Array.from(new Set(items.map(m => m.category)));
        return {
            type,
            categories: categories.map(cat => ({
                category: cat,
                morphemes: items.filter(m => m.category === cat).sort((a, b) => a.sortOrder - b.sortOrder),
            })),
        };
    }).filter(g => g.categories.length > 0);

    const getMeaning = (m: Morpheme): string => {
        return m.meaning[lang] || m.meaning['en'] || '';
    };

    const getDisplay = (m: Morpheme): string => {
        if (m.type === 'prefix') return `${m.morpheme}-`;
        if (m.type === 'suffix') return `-${m.morpheme}`;
        return m.morpheme;
    };

    const getAnchorId = (m: Morpheme): string => {
        return `${m.type}-${m.morpheme}`;
    };

    if (loading) {
        return (
            <div className="morph-ref">
                <div className="morph-ref__loading">{t('common:loading', 'Loading...')}</div>
            </div>
        );
    }

    return (
        <div className="morph-ref">
            <div className="morph-ref__header">
                <Link to="/knowledge-graph" className="morph-ref__back">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
                    </svg>
                    {t('backToGraph', 'Knowledge Graph')}
                </Link>
                <h1 className="morph-ref__title">{t('title', 'Morphology Reference')}</h1>
                <p className="morph-ref__subtitle">{t('subtitle', 'Prefixes, suffixes, and roots — the building blocks of English words')}</p>
            </div>

            {/* Intro */}
            <div className="morph-ref__intro">
                <p>{t('intro', 'Understanding morphemes (the smallest meaningful units of words) helps you decode unfamiliar vocabulary. Recognizing a prefix like "un-" or a root like "struct" lets you infer meaning even for words you\'ve never seen before.')}</p>
            </div>

            {/* Stats */}
            <div className="morph-ref__stats">
                <div className="morph-ref__stat">
                    <span className="morph-ref__stat-value">{morphemes.filter(m => m.type === 'prefix').length}</span>
                    <span className="morph-ref__stat-label">{t('prefixes', 'Prefixes')}</span>
                </div>
                <div className="morph-ref__stat">
                    <span className="morph-ref__stat-value">{morphemes.filter(m => m.type === 'suffix').length}</span>
                    <span className="morph-ref__stat-label">{t('suffixes', 'Suffixes')}</span>
                </div>
                <div className="morph-ref__stat">
                    <span className="morph-ref__stat-value">{morphemes.filter(m => m.type === 'root').length}</span>
                    <span className="morph-ref__stat-label">{t('roots', 'Roots')}</span>
                </div>
            </div>

            {/* TOC */}
            <nav className="morph-ref__toc">
                <h2 className="morph-ref__toc-title">{t('toc', 'Table of Contents')}</h2>
                {grouped.map(g => (
                    <div key={g.type} className="morph-ref__toc-section">
                        <span className={`morph-ref__toc-type morph-ref__toc-type--${g.type}`}>
                            {t(`typeLabel.${g.type}`)}
                        </span>
                        <div className="morph-ref__toc-cats">
                            {g.categories.map(c => (
                                <div key={c.category} className="morph-ref__toc-cat">
                                    <span className="morph-ref__toc-cat-name">
                                        {c.category.replace(/_/g, ' ')}
                                        {!isEn && <span className="morph-ref__native-label">{t(`knowledgeGraph:foundations.categoryLabel.${c.category}`, '')}</span>}
                                    </span>
                                    <div className="morph-ref__toc-items">
                                        {c.morphemes.map(m => (
                                            <a key={m.id} href={`#${getAnchorId(m)}`} className="morph-ref__toc-item">
                                                {getDisplay(m)}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Content sections */}
            {grouped.map(g => (
                <div key={g.type} className="morph-ref__type-section">
                    <h2 className={`morph-ref__type-title morph-ref__type-title--${g.type}`}>
                        {t(`typeLabel.${g.type}`)}
                        <span className="morph-ref__type-desc">{t(`typeDesc.${g.type}`)}</span>
                    </h2>

                    {g.categories.map(c => (
                        <div key={c.category} className="morph-ref__cat-section">
                            <h3 className="morph-ref__cat-title">
                                {c.category.replace(/_/g, ' ')}
                                {!isEn && <span className="morph-ref__native-label">{t(`knowledgeGraph:foundations.categoryLabel.${c.category}`, '')}</span>}
                            </h3>

                            <div className="morph-ref__morpheme-grid">
                                {c.morphemes.map(m => (
                                    <div key={m.id} id={getAnchorId(m)} className="morph-ref__card">
                                        <div className="morph-ref__card-top">
                                            <span className={`morph-ref__morpheme-display morph-ref__morpheme-display--${m.type}`}>
                                                {getDisplay(m)}
                                            </span>
                                            <span className="morph-ref__cefr" data-level={m.cefrLevel}>
                                                {m.cefrLevel}
                                            </span>
                                        </div>
                                        <p className="morph-ref__meaning">{getMeaning(m)}</p>
                                        {m.examples.length > 0 && (
                                            <div className="morph-ref__examples">
                                                {m.examples.slice(0, 4).map((ex, i) => (
                                                    <span key={i} className="morph-ref__example">{ex}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default MorphologyReferencePage;
